import React from "react"
import { cn } from "../utils"
import { TabLinks } from "../tab-links"
import { usePageHeaderContext } from "../page-header/page-header-context"
import { PageSecondaryHeaderCenterContent } from "./page-secondary-header-center-content"
import { pageSecondaryHeaderTabContainerStyles } from "./page-secondary-header-tab-container-styles"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderTabLinksProps = {
  accessibleName: string
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderTabLinksStyles = [
  // With the center content's `-mx-1`, offsets the gutter of `TabLinks`
  "mx-[calc(0.25rem_-_var(--tab-links-gutter))]",
  ...pageSecondaryHeaderTabContainerStyles
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header tab links (cross-page navigation).
 *
 * Composes `TabLinks`: a `<nav>` landmark for navigation that *visually* looks
 * like tabs but moves the user between pages (each link has its own URL).
 * Compose with `PageSecondaryHeader.TabLink` for each link; the active link is
 * derived from the ancestor `PageHeader`'s `currentPath`.
 *
 * For in-page panel switching (same URL, switching content), use
 * `PageSecondaryHeader.Tabs` instead — it renders a real `role="tablist"` with
 * arrow-key navigation and shared state via `PageSecondaryHeader.TabsRoot`.
 *
 * The accessible name differentiates this navigation landmark from the main
 * `<Navigation>` component (e.g. "Broadcast sections").
 */
export function PageSecondaryHeaderTabLinks({
  accessibleName,
  children,
  className
}: PageSecondaryHeaderTabLinksProps) {
  const { currentPath } = usePageHeaderContext()

  return (
    <PageSecondaryHeaderCenterContent>
      <TabLinks
        accessibleName={accessibleName}
        className={cn(pageSecondaryHeaderTabLinksStyles, className)}
        currentPath={currentPath}
      >
        {children}
      </TabLinks>
    </PageSecondaryHeaderCenterContent>
  )
}
